import { type Reaction } from "@/data/reactions";
import { motion } from "framer-motion";

interface EnergyDiagramProps {
  reaction: Reaction;
}

export default function EnergyDiagram({ reaction }: EnergyDiagramProps) {
  const isExo = reaction.energy === "exothermic";

  // Energy levels (relative)
  const reactantY = isExo ? 60 : 140;
  const productY = isExo ? 140 : 60;
  const activationY = 30; // Peak always at top

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium text-foreground mb-3">Energy Diagram</p>
      <div className="relative">
        <svg viewBox="0 0 300 180" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
          {/* Axes */}
          <line x1="40" y1="165" x2="290" y2="165" stroke="hsl(var(--border))" strokeWidth="1.5" />
          <line x1="40" y1="165" x2="40" y2="10" stroke="hsl(var(--border))" strokeWidth="1.5" />

          {/* Axis labels */}
          <text x="165" y="178" textAnchor="middle" className="fill-muted-foreground" fontSize="9">
            Reaction Progress
          </text>
          <text x="12" y="90" textAnchor="middle" className="fill-muted-foreground" fontSize="9" transform="rotate(-90, 12, 90)">
            Energy
          </text>

          {/* Energy curve */}
          <motion.path
            d={`M 60 ${reactantY} C 100 ${reactantY}, 120 ${activationY}, 165 ${activationY} C 210 ${activationY}, 230 ${productY}, 270 ${productY}`}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
          />

          {/* Reactant level line */}
          <line x1="45" y1={reactantY} x2="80" y2={reactantY} stroke="hsl(var(--muted-foreground))" strokeWidth="1" strokeDasharray="3 3" />
          {/* Product level line */}
          <line x1="250" y1={productY} x2="285" y2={productY} stroke="hsl(var(--muted-foreground))" strokeWidth="1" strokeDasharray="3 3" />

          {/* ΔH arrow */}
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            <line
              x1="280"
              y1={reactantY}
              x2="280"
              y2={productY}
              stroke={isExo ? "hsl(0, 70%, 55%)" : "hsl(210, 70%, 55%)"}
              strokeWidth="1.5"
              markerEnd="url(#arrowhead)"
            />
            <defs>
              <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="3" refY="2" orient="auto">
                <polygon points="0 0, 6 2, 0 4" fill={isExo ? "hsl(0, 70%, 55%)" : "hsl(210, 70%, 55%)"} />
              </marker>
            </defs>
            <text
              x="275"
              y={(reactantY + productY) / 2 + 3}
              textAnchor="end"
              fontSize="8"
              fill={isExo ? "hsl(0, 70%, 55%)" : "hsl(210, 70%, 55%)"}
              fontWeight="600"
            >
              ΔH {isExo ? "< 0" : "> 0"}
            </text>
          </motion.g>

          {/* Labels */}
          <text x="55" y={reactantY - 8} textAnchor="start" className="fill-foreground" fontSize="9" fontWeight="500">
            Reactants
          </text>
          <text x="255" y={productY - 8} textAnchor="middle" className="fill-foreground" fontSize="9" fontWeight="500">
            Products
          </text>
          <text x="165" y={activationY - 6} textAnchor="middle" className="fill-muted-foreground" fontSize="8">
            Ea (activation)
          </text>
        </svg>
      </div>
    </div>
  );
}
